import React, {useCallback, useMemo, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import Card from "../../../components/card";
import InputForm from "../../../components/input-form";
import Button from "../../../components/button";
import TrainingUsersModal from "./training-users-modal";
import ImportData from "./import-modal";
import {setAccentInsensitiveQuickFilter} from "../../../utils/ag-grid-quick-filter";
import {sortTrainingUsersByName} from "./training-users-utils";
import {FaDownload, FaFileImport, FaFilePdf, FaPaperPlane, FaPlus} from "react-icons/fa";

const TrainingUsersTable = ({
    users,
    onChange,
    setIsLoading,
    certificateActionsDisabled = true,
    generatingCertificateUserIds = [],
    onGenerateCertificate,
    onSendCertificate,
    onDownloadCertificate,
}) => {
    const gridRef = useRef();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const sortedUsers = useMemo(() => sortTrainingUsersByName(users), [users]);
    const userColumns = useMemo(() => [
        {field: "name", headerName: "Nome", flex: 1, minWidth: 180},
        {field: "cpf", headerName: "CPF", flex: 1, minWidth: 120},
        {field: "project", headerName: "Projeto", flex: 1, minWidth: 120},
        {field: "resale", headerName: "Revenda", flex: 1, minWidth: 120},
        {
            field: "certificate_actions",
            headerName: "Certificado",
            sortable: false,
            filter: false,
            width: 165,
            cellRenderer: ({data}) => (
                <div className="flex h-full items-center justify-center gap-2">
                    <Button
                        type="button"
                        target="primary"
                        title="Enviar certificado"
                        disabled={certificateActionsDisabled || data?.certificate_generated !== true || !data?.user_id}
                        onClick={() => onSendCertificate?.(data)}
                    >
                        <FaPaperPlane />
                    </Button>

                    <Button
                        type="button"
                        target="primary"
                        title="Baixar certificado"
                        disabled={certificateActionsDisabled || data?.certificate_generated !== true || !data?.user_id}
                        onClick={() => onDownloadCertificate?.(data)}
                    >
                        <FaDownload />
                    </Button>
                    {data?.id && data?.certificate_generated !== true && (
                        <Button
                            type="button"
                            target="primary"
                            title="Gerar certificado"
                            disabled={certificateActionsDisabled || generatingCertificateUserIds.includes(data.user_id)}
                            onClick={() => onGenerateCertificate?.(data)}
                        >
                            <FaFilePdf />
                        </Button>
                    )}
                </div>
            ),
        },
    ], [certificateActionsDisabled, generatingCertificateUserIds, onDownloadCertificate, onGenerateCertificate, onSendCertificate]);

    const onFilterTextBoxChanged = useCallback(({target}) => {
        setAccentInsensitiveQuickFilter(gridRef, target.value);
    }, []);

    return (
        <>
            <Card title="Usuários" wrapperClass="h-full min-h-0 min-w-0 xl:flex xl:flex-col" bodyClass="min-h-0 min-w-0 xl:flex xl:flex-1 xl:flex-col">
                <div className="mb-3 flex flex-wrap gap-4">
                    <InputForm
                        id="search-training-users"
                        label="Pesquisar"
                        type="search"
                        col="min-w-[220px] flex-1"
                        onChange={onFilterTextBoxChanged}
                    />

                    <Button type="button" col="self-end" target="primary" onClick={() => setIsModalOpen(true)} title="Adicionar usuários">
                        <FaPlus/>
                    </Button>
                    <Button type="button" col="self-end" target="success" onClick={() => setIsImportModalOpen(true)} title="Importar usuários">
                        <FaFileImport />
                    </Button>
                </div>

                <div className="ag-theme-quartz h-[320px] w-full min-w-0 xl:flex-1">
                    <AgGridReact
                        ref={gridRef}
                        columnDefs={userColumns}
                        rowData={sortedUsers}
                        defaultColDef={{resizable: true, sortable: true}}
                        overlayNoRowsTemplate="Nenhum usuário vinculado"
                    />
                </div>
            </Card>

            <TrainingUsersModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                selectedUsers={sortedUsers}
                onConfirm={onChange}
            />

            <ImportData
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                selectedUsers={sortedUsers}
                onConfirm={onChange}
                setIsLoading={setIsLoading}
            />
        </>
    );
};

export default TrainingUsersTable;
